import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { projectService, caseService } from '../services/api';
import CreateProjectModal from '../components/CreateProjectModal';

const Dashboard = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [recentCases, setRecentCases] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [cerca, setCerca] = useState('');

  useEffect(() => {
    fetchDashboardData();
    
    // Refresh colleagues status every 30 seconds
    const interval = setInterval(async () => {
      try {
        const colleaguesData = await projectService.getWorkspaceColleagues();
        setColleagues(colleaguesData);
      } catch (error) {
        console.error('Error refreshing colleagues:', error);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch projects first
      const projectsData = await projectService.getProjects(true);
      setProjects(projectsData);
      
      // Try to fetch colleagues, but don't let it fail the whole dashboard
      try {
        const colleaguesData = await projectService.getWorkspaceColleagues();
        setColleagues(colleaguesData);
      } catch (colleaguesError) {
        console.error('Error fetching colleagues:', colleaguesError);
        console.error('Colleagues error details:', colleaguesError.response?.data);
        if (colleaguesError.response?.data?.detail) {
          console.error('Detailed error:', JSON.stringify(colleaguesError.response.data.detail, null, 2));
        }
        setColleagues([]);
      }
      
      // Tots els espais, no els tres primers, i tots els seus casos, no els
      // cinc més recents: el tauler és la llista de la feina i retallar-la vol
      // dir amagar casos sense dir-ho. El servidor ja només retorna els que et
      // pertoquen segons el teu rol.
      const cases = await Promise.all(projectsData.map(async (project) => {
        try {
          const c = await caseService.getCases(project.id);
          return c.map(x => ({ ...x, project_id: project.id, project_name: project.name }));
        } catch (err) {
          console.error(`Error fetching cases for project ${project.id}:`, err);
          return [];
        }
      }));
      const allCases = cases.flat().filter(c => !c.is_archived);
      allCases.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      setRecentCases(allCases);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectService.getProjects(true);
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleCreateProject = async (projectData) => {
    try {
      await projectService.createProject(projectData);
      setShowCreateModal(false);
      fetchProjects();
    } catch (error) {
      toast.error(t('dashboard.createError'));
      console.error('Error creating workspace:', error);
    }
  };

  // Els casos que es veuen, i quants en queden per acabar.
  const cercaNeta = cerca.trim().toLowerCase();
  const casosVisibles = recentCases.filter(c => !cercaNeta
    || [c.name, c.project_name].some(x => (x || '').toLowerCase().includes(cercaNeta)));
  const casosPendents = recentCases.filter(c => (c.progress_step || 1) <= 6);
  // La columna de l'espai només té sentit si en tens més d'un.
  const mesDunEspai = new Set(recentCases.map(c => c.project_id)).size > 1;

  if (loading) {
    return <div className="py-12 text-center text-sm text-neutral-500">{t('common.loading')}</div>;
  }

  // Aquí hi havia `stats` i `getActivityLevel`, que calculaven quants espais
  // tens, quants n'has tocat aquesta setmana i en deien «activitat alta,
  // mitjana o baixa». Cap dels dos números feia avançar ningú, i l'«activitat»
  // era una nota inventada sobre la teva feina.

  return (
    <div className="space-y-6">
      {/* ══════════════════════════════════════════════════════════════════
          El tauler (redisseny de l'11/08/2026)

          Abans donava la benvinguda amb el nom en lletra grossa, deia que «el
          sistema està actiu», la data d'avui i dos comptadors, i després
          ensenyava fins a quatre casos i fins a quatre espais.

          Res d'allò responia la pregunta amb què s'entra al tauler, que sempre
          és la mateixa: **on ho vaig deixar i què em toca ara**. Que el sistema
          està actiu ho saps perquè el veus, i la data la porta l'ordinador.

          Ara hi ha una fila per cas amb **en quina passa és i què hi toca
          fer**. Val per als tres rols perquè la pregunta és la mateixa per a
          tots tres; el que canvia és **quantes files** hi ha, i per això la
          columna de l'espai només surt quan n'hi ha més d'un i la cerca només
          apareix a partir de vuit casos.
          ══════════════════════════════════════════════════════════════════ */}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">
            {t('dashboard.greeting', { name: currentUser?.full_name?.split(' ')[0] || currentUser?.username })}
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            {casosVisibles.length === 0
              ? t('dashboard.noCasesLead')
              : t('dashboard.casesLead', { count: casosPendents.length, total: casosVisibles.length })}
          </p>
        </div>
        {(currentUser?.role === 'administrator' || currentUser?.role === 'instructor') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-700"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('dashboard.createWorkspace')}
          </button>
        )}
      </div>

      {/* La cerca només quan comença a caldre. Amb tres casos és soroll. */}
      {recentCases.length > 7 && (
        <div className="relative max-w-sm">
          <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
               fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            placeholder={t('dashboard.searchPlaceholder')}
            className="w-full rounded-md border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm text-neutral-900 placeholder-neutral-400 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
          />
        </div>
      )}

      {casosVisibles.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 px-4 py-14 text-center">
          <p className="text-sm text-neutral-600">
            {recentCases.length === 0 ? t('dashboard.noCasesYet') : t('dashboard.noMatches')}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    {t('dashboard.colCase')}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    {t('dashboard.colWhere')}
                  </th>
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    {t('dashboard.colNext')}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    {t('dashboard.colUpdated')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {casosVisibles.map((case_) => {
                  const passa = case_.progress_step || 1;
                  const acabat = passa > 6;
                  return (
                    <tr key={case_.id} className="border-b border-neutral-200 align-top last:border-b-0 hover:bg-neutral-50">
                      <td className="min-w-[13rem] px-4 py-3">
                        <Link
                          to={`/workspaces/${case_.project_id}/cases/${case_.id}`}
                          className="font-medium text-neutral-900 hover:text-accent-700"
                        >
                          {case_.name}
                        </Link>
                        <span className="mt-0.5 block text-xs text-neutral-500">
                          {mesDunEspai && <>{case_.project_name} · </>}
                          {case_.time_horizon || t('common.notSpecified')}
                        </span>
                      </td>

                      {/* On és: sis marques, les fetes plenes. Una barra de sis
                          trams diu el mateix que un percentatge i no convida a
                          llegir-ho com una nota. */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="flex gap-[3px]" aria-hidden="true">
                          {[1, 2, 3, 4, 5, 6].map(n => (
                            <span key={n} className={`block h-[3px] w-4 rounded-sm ${
                              n < passa ? 'bg-accent-600'
                                : n === passa && !acabat ? 'bg-accent-600 ring-2 ring-accent-100'
                                : 'bg-neutral-200'
                            }`} />
                          ))}
                        </span>
                        <span className="mt-1.5 block text-xs text-neutral-500">
                          {acabat
                            ? t('dashboard.stepDone')
                            : t('dashboard.stepOf', { step: passa, name: t(`wizard.step${passa}Short`) })}
                        </span>
                      </td>

                      <td className="max-w-[34ch] px-4 py-3 text-neutral-700">
                        {acabat ? (
                          <span className="text-neutral-500">{t('dashboard.nextDone')}</span>
                        ) : (
                          t(`dashboard.next.${passa}`)
                        )}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 font-mono text-[11px] text-neutral-500">
                        {new Date(case_.updated_at || case_.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Els espais i la gent, a sota i en petit: es consulten de tant en tant,
          no cada vegada que s'obre el tauler. */}
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <p className="mb-2 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-neutral-400">
            {t('dashboard.recentWorkspaces')}
          </p>
          {projects.length === 0 ? (
            <p className="text-sm text-neutral-500">{t('dashboard.noWorkspacesYet')}</p>
          ) : (
            <ul className="space-y-1.5">
              {projects.map(p => (
                <li key={p.id}>
                  <Link
                    to={`/workspaces/${p.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-neutral-200 px-3 py-2 text-sm hover:border-neutral-300"
                  >
                    <span className="font-medium text-neutral-900">{p.name}</span>
                    <span className="font-mono text-[11px] text-neutral-500">
                      {t('dashboard.casesCount', { count: p.cases_count || 0 })}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="mb-2 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-neutral-400">
            {t('dashboard.members')}
          </p>
          <ul className="space-y-1.5">
            <li className="flex items-center gap-2.5 rounded-md border border-neutral-200 px-3 py-2 text-sm">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-100 text-[10px] font-semibold text-accent-700">
                {(currentUser?.full_name || currentUser?.username)?.charAt(0)?.toUpperCase()}
              </span>
              <span className="text-neutral-900">{currentUser?.full_name || currentUser?.username}</span>
              <span className="ml-auto font-mono text-[10px] text-neutral-400">{t('dashboard.you')}</span>
            </li>
            {colleagues.map(c => (
              <li key={c.id} className="flex items-center gap-2.5 rounded-md border border-neutral-200 px-3 py-2 text-sm">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-100 text-[10px] font-semibold text-neutral-600">
                  {(c.full_name || c.username)?.charAt(0)?.toUpperCase()}
                </span>
                <span className="text-neutral-900">{c.full_name || c.username}</span>
                {c.is_online && (
                  <span className="ml-auto font-mono text-[10px] text-neutral-400">{t('dashboard.online')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateProject}
        />
      )}
    </div>
  );
};

export default Dashboard;